import { Award, Compass, Eye, HeartHandshake, ShieldCheck, Target } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import { Badge } from "@/components/ui/badge";

export default function About() {
  return (
    <div className="bg-white pt-24 sm:pt-28" data-testid="page-about">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">About Samota Insurance</Badge>
            <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              A family business <span className="text-gradient-blue">protecting families.</span>
            </h1>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Samota Insurance n Investments was founded in <strong>1987</strong> — a true family
              business built on a simple belief that financial advice should be honest, lifelong,
              and free of jargon. For nearly four decades we've helped individuals, families and
              businesses across Thane, Mumbai and India protect what matters and grow what they've built.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Today, the firm is led by <strong>Vinay Samota, CFP®</strong>, who brings 20+ years
              of his own advisory experience to the legacy his family built. With an
              <strong> able team of 8</strong>, we've placed <strong>5,000+ policies</strong>
              and <strong>₹500 Cr+</strong> in sum-assured for over <strong>1,000 families</strong> —
              while remaining small enough to know every client by name.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_trust-first-advisory/artifacts/9ev1nz8o_Vinay%20Samota%20AI%20generated%20pic.jpeg"
              alt="Vinay Samota, CFP®"
              className="rounded-3xl soft-shadow-lg w-full object-cover aspect-[4/5] sm:aspect-square"
            />
            <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl bg-white p-5 border border-slate-100 soft-shadow w-60">
              <div className="text-xs uppercase tracking-wider text-slate-500">Founder</div>
              <div className="font-display font-semibold text-slate-900 mt-1">Vinay Samota, CFP®</div>
              <div className="text-xs text-slate-500 mt-0.5">Certified Financial Planner</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          {[
            { Icon: Target, title: "Mission", body: "Help families and businesses make informed financial decisions through transparent advice, unbiased recommendations and lifelong service." },
            { Icon: Eye, title: "Vision", body: "Be the most trusted insurance and financial-planning firm in Maharashtra — known for ethics, expertise and empathy." },
            { Icon: HeartHandshake, title: "Values", body: "Client-first thinking. Transparency. Education. Long-term relationships. Real claim ownership." },
          ].map(({ Icon, title, body }) => (
            <div key={title} data-testid={`about-pillar-${title.toLowerCase()}`} className="rounded-2xl bg-white p-8 border border-slate-100 soft-shadow">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-900 text-white">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-5 text-xl font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50/60">Founder</Badge>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
              Meet Vinay Samota, <span className="text-gradient-blue">CFP®</span>
            </h2>
            <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
              <p>
                A Certified Financial Planner with 20+ years of advisory experience,
                Vinay has guided thousands of families through life's biggest financial
                decisions — buying a first home, planning a child's education,
                navigating a parent's hospitalisation, or scaling a business.
              </p>
              <p>
                His philosophy is simple: insurance is not a product to be sold once,
                it's a relationship that's tested at the worst possible moment. That's
                why every Samota client gets a single, accountable point of contact for
                renewals, reviews and — most importantly — claims.
              </p>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { Icon: Award, t: "CFP®", s: "Certified Financial Planner credential" },
                { Icon: ShieldCheck, t: "IRDAI Authorised", s: "Regulated insurance intermediary" },
                { Icon: Compass, t: "Multi-product Expertise", s: "Life, health, general & investments" },
                { Icon: HeartHandshake, t: "20+ Years", s: "Vinay's personal advisory experience" },
              ].map(({ Icon, t, s }) => (
                <div key={t} className="flex gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-900 shrink-0">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t}</div>
                    <div className="text-xs text-slate-500 leading-relaxed mt-0.5">{s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <LeadForm
            title="Speak with Vinay personally"
            subtitle="Founder-led consultations available by appointment, in-person or over video."
            source="about_page"
            testIdPrefix="about"
          />
        </div>
      </section>
    </div>
  );
}
